
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { createContext } from "react";
import Auth from "./pages/Auth";
import Index from "./pages/Index";
import Contacts from "./pages/Contacts";
import Leads from "./pages/Leads";
import Projects from "./pages/Projects";
import ProjectDashboard from "./pages/ProjectDashboard";
import ProjectTracking from "./pages/ProjectTracking";
import Tasks from "./pages/Tasks";
import ChangePassword from "./pages/ChangePassword";
import OrganizationSettings from "./pages/OrganizationSettings";
import OrganizationMembers from "./pages/OrganizationMembers";
import SharedProject from "./pages/SharedProject";
import { Toaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { OrganizationProvider } from "@/contexts/OrganizationContext";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      refetchOnWindowFocus: false,
    },
  },
});

export const UserRoleContext = createContext<{
  userRole: string;
  setUserRole: (role: string) => void;
}>({ userRole: "", setUserRole: () => {} });

const AppRoutes = () => {
  const { session, userRole, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <Routes>
      <Route
        path="/auth"
        element={!session ? <Auth /> : <Navigate to="/" replace />}
      />
      <Route
        path="/"
        element={session ? <Index /> : <Navigate to="/auth" replace />}
      />
      <Route
        path="/contacts"
        element={session ? <Contacts /> : <Navigate to="/auth" replace />}
      />
      <Route
        path="/leads"
        element={session ? <Leads /> : <Navigate to="/auth" replace />}
      />
      <Route
        path="/project-dashboard"
        element={session ? <ProjectDashboard /> : <Navigate to="/auth" replace />}
      />
      <Route
        path="/projects"
        element={
          session && userRole === 'salesman' 
            ? <Projects /> 
            : session 
              ? <Navigate to="/" replace /> 
              : <Navigate to="/auth" replace />
        }
      />
      <Route
        path="/projects/:projectId/tracking"
        element={
          session && userRole === 'salesman' 
            ? <ProjectTracking /> 
            : session 
              ? <Navigate to="/" replace /> 
              : <Navigate to="/auth" replace />
        }
      />
      <Route
        path="/tasks"
        element={session ? <Tasks /> : <Navigate to="/auth" replace />}
      />
      <Route
        path="/change-password"
        element={session ? <ChangePassword /> : <Navigate to="/auth" replace />}
      />
      <Route
        path="/organization/settings"
        element={session ? <OrganizationSettings /> : <Navigate to="/auth" replace />}
      />
      <Route
        path="/organization/members"
        element={session ? <OrganizationMembers /> : <Navigate to="/auth" replace />}
      />
      <Route path="/shared/project/:token" element={<SharedProject />} />
    </Routes>
  );
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <AuthProvider>
          <OrganizationProvider>
            <Router>
              <AppRoutes />
              <Toaster />
            </Router>
          </OrganizationProvider>
        </AuthProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
}

export default App;
